import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { RefreshOutlined, VisibilityOutlined } from '@mui/icons-material';
import { fetchAdCopy, regenerateAdCopy, AdCopyRow } from '../../helpers/adEvaluationData';
import { formatNumber } from '../../helpers/formatNumber';
import moment from 'moment';

const AdCopyTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AdCopyRow[]>([]);
  const [avgCpa, setAvgCpa] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [previewRow, setPreviewRow] = useState<AdCopyRow | null>(null);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const [confirmRow, setConfirmRow] = useState<AdCopyRow | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAdCopy();
      setData(result.rows);
      setAvgCpa(result.avgCpaPerRegistration);
    } catch (err) {
      console.error('Error fetching ad copy:', err);
      setError('Fehler beim Laden der Ad Copy Daten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRegenerate = async (row: AdCopyRow) => {
    const key = `${row.product_type}-${row.product_id}`;
    setRegeneratingId(key);
    setConfirmRow(null);
    try {
      await regenerateAdCopy(row.product_type, row.product_id);
      await loadData();
    } catch (err) {
      console.error('Error regenerating ad copy:', err);
      setError('Fehler beim Regenerieren');
    } finally {
      setRegeneratingId(null);
    }
  };

  const truncate = (text: string, maxLen: number) => {
    if (!text || text.length <= maxLen) return text || '';
    return text.substring(0, maxLen) + '...';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (data.length === 0) {
    return <Alert severity="info">Keine gecachte Ad Copy vorhanden.</Alert>;
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }} icon={false}>
        <Typography variant="body2">
          <strong>Ø CPA pro Webinar-Registrierung:</strong> {formatNumber(avgCpa, 2)} €
          (90-Tage-Durchschnitt, nicht-evergreen Webinare). Webinar-CPA-Umsatz = Regs × Ø CPA
          (Schätzwert). Abo-CPA-Umsatz = CPA-Wert je Bestellung (Anfo-Wert × CPA-Anteil). Klicks werden nicht
          getrackt (EPOM client-side).
        </Typography>
      </Alert>
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                { label: 'Typ', align: 'left' as const },
                { label: 'Produktname', align: 'left' as const },
                { label: 'Headline', align: 'left' as const },
                { label: 'CTA', align: 'left' as const },
                {
                  label: 'Regs (Var. / ges.)',
                  align: 'right' as const,
                  tooltip:
                    'Webinar-Registrierungen über Artikel-Ads. Links: nur diese Copy-Variante (Banner _c{copy_id}v{version}). Rechts: alle Versionen des Produkts (inkl. Pre-2026-06-08-Conversions ohne Suffix).',
                },
                {
                  label: 'Teiln. (Var. / ges.)',
                  align: 'right' as const,
                  tooltip:
                    'Webinar-Teilnehmer (attendance-Event). Var. = nur diese Copy-Variante, ges. = alle Versionen des Produkts.',
                },
                {
                  label: 'Best. (Var. / ges.)',
                  align: 'right' as const,
                  tooltip:
                    'Abo: Direkt-Bestellungen über Artikel-Ads (nicht abgelehnt). Webinar: Bestellungen, die zu Registrierungen über Artikel-Ads gehören (webinar_order_mapping). Var. = nur diese Copy-Variante, ges. = alle Versionen des Produkts.',
                },
                {
                  label: 'CPA-Umsatz (Var. / ges.)',
                  align: 'right' as const,
                  tooltip:
                    'CPA-Wert der Bestellungen. Webinar: Bestellungen, die zu Registrierungen über Artikel-Ads gehören (bis 16.09.2026 Schätzung Regs × Ø CPA). Abo: Direkt-Bestellungen. Var. = nur diese Copy-Variante.',
                },
                { label: 'Generiert', align: 'left' as const },
                { label: 'Aktionen', align: 'left' as const },
              ].map((col) => (
                <TableCell
                  key={col.label}
                  align={col.align}
                  sx={{
                    backgroundColor: '#e0e0e0 !important',
                    fontWeight: 'bold',
                  }}
                >
                  {col.tooltip ? (
                    <Tooltip title={col.tooltip}>
                      <span>{col.label}</span>
                    </Tooltip>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => {
              const key = `${row.product_type}-${row.product_id}`;
              const isRegenerating = regeneratingId === key;
              const isWebinar = row.product_type === 'webinar';
              return (
                <TableRow key={key}>
                  <TableCell>
                    <Chip
                      label={isWebinar ? 'Webinar' : 'Abo'}
                      size="small"
                      color={isWebinar ? 'info' : 'warning'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{row.product_name || row.product_id}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 250,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {truncate(row.headline, 60)}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.cta_text}</TableCell>
                  <TableCell align="right">
                    {isWebinar
                      ? `${formatNumber(row.variant_registrations || 0, 0)} / ${formatNumber(row.registrations || 0, 0)}`
                      : '–'}
                  </TableCell>
                  <TableCell align="right">
                    {isWebinar
                      ? `${formatNumber(row.variant_attendees || 0, 0)} / ${formatNumber(row.attendees || 0, 0)}`
                      : '–'}
                  </TableCell>
                  <TableCell align="right">
                    {`${formatNumber(row.variant_orders || 0, 0)} / ${formatNumber(row.orders || 0, 0)}`}
                  </TableCell>
                  <TableCell align="right">
                    {`${formatNumber(row.variant_cpa_revenue || 0, 2)} / ${formatNumber(row.cpa_revenue || 0, 2)} €`}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      {row.generated_at ? (
                        <span>{moment(row.generated_at).format('DD.MM.YYYY HH:mm')}</span>
                      ) : (
                        <span>–</span>
                      )}
                      {row.version != null && (
                        <Tooltip
                          title={`Copy-ID ${row.copy_id ?? '?'} · attribution-tag: _c${row.copy_id ?? '?'}v${row.version}`}
                        >
                          <Chip
                            label={`v${row.version}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.7rem' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Vorschau">
                        <IconButton size="small" onClick={() => setPreviewRow(row)}>
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Regenerieren">
                        <IconButton
                          size="small"
                          onClick={() => setConfirmRow(row)}
                          disabled={isRegenerating}
                        >
                          {isRegenerating ? (
                            <CircularProgress size={18} />
                          ) : (
                            <RefreshOutlined fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Preview Dialog */}
      <Dialog open={!!previewRow} onClose={() => setPreviewRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Ad Copy Vorschau — {previewRow?.product_name || previewRow?.product_id}
        </DialogTitle>
        <DialogContent>
          {previewRow && (
            <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1, mt: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {previewRow.headline.replace(/\{ASSET_NAME\}/g, 'Musteraktie')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                {previewRow.body.replace(/\{ASSET_NAME\}/g, 'Musteraktie')}
              </Typography>
              <Chip
                label={previewRow.cta_text.replace(/\{ASSET_NAME\}/g, 'Musteraktie')}
                color="primary"
                sx={{ mt: 2 }}
              />
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Generiert: {moment(previewRow.generated_at).format('DD.MM.YYYY HH:mm')}
                  {previewRow.version != null && ` | Version: v${previewRow.version}`}
                  {previewRow.copy_id != null &&
                    ` | Banner-Tag: _c${previewRow.copy_id}v${previewRow.version}`}
                  {previewRow.model_used && ` | Modell: ${previewRow.model_used}`}
                  {previewRow.prompt_id && ` | Prompt: ${previewRow.prompt_id}`}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewRow(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Regenerate Dialog */}
      <Dialog open={!!confirmRow} onClose={() => setConfirmRow(null)}>
        <DialogTitle>Ad Copy regenerieren?</DialogTitle>
        <DialogContent>
          <Typography>
            Die bestehende Ad Copy für &quot;
            {confirmRow?.product_name || confirmRow?.product_id}&quot; wird gelöscht und neu
            generiert. Dieser Vorgang kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRow(null)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => confirmRow && handleRegenerate(confirmRow)}
          >
            Regenerieren
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdCopyTab;
